import { CURRENT_CLIENT } from '@/constants'

export type SettingsSection = {
  /** Route segment under `/settings/` (e.g. `account`, `billing`). */
  segment: string
  /** Sidebar nav label and page `<h1>` — must match. */
  label: string
  icon: string
  /** Page subtitle and sidebar tooltip. */
  description: string
  kind: 'account' | 'notifications' | 'placeholder'
}

const BASE_SETTINGS_SECTIONS: SettingsSection[] = [
  {
    segment: 'account',
    label: 'Account',
    icon: 'User',
    description: 'Manage your photo, name, and email.',
    kind: 'account',
  },
  {
    segment: 'preferences',
    label: 'Preferences',
    icon: 'SlidersHorizontal',
    description: 'Appearance, language, and workspace defaults.',
    kind: 'placeholder',
  },
  {
    segment: 'notifications',
    label: 'Notifications',
    icon: 'Bell',
    description: 'Email and in-app notification preferences.',
    kind: 'notifications',
  },
  {
    segment: 'security',
    label: 'Security & access',
    icon: 'Shield',
    description: 'Password, sessions, and sign-in security.',
    kind: 'placeholder',
  },
  {
    segment: 'connected-accounts',
    label: 'Connected accounts',
    icon: 'Link2',
    description: 'OAuth providers and linked sign-in methods.',
    kind: 'placeholder',
  },
  {
    segment: 'connectors',
    label: 'Connectors',
    icon: 'Plug',
    description: 'Integrations and data sources connected to your workspace.',
    kind: 'placeholder',
  },
  {
    segment: 'skills-knowledge',
    label: 'Skills & Knowledge',
    icon: 'BookOpen',
    description: 'Agent skills, knowledge bases, and reference material.',
    kind: 'placeholder',
  },
]

type SettingsSectionsToggle = Partial<Record<string, boolean>>

function isPlaceholderSectionEnabled(segment: string): boolean {
  const toggles = (CURRENT_CLIENT as { settings?: { sections?: SettingsSectionsToggle } }).settings
    ?.sections
  if (!toggles || !(segment in toggles)) return true
  return toggles[segment] === true
}

function placeholderSections(): SettingsSection[] {
  return BASE_SETTINGS_SECTIONS.slice(1).filter((section) =>
    isPlaceholderSectionEnabled(section.segment)
  )
}

/** Ordered settings sections for the active tenant (account + enabled placeholders). */
export function getSettingsSections(): SettingsSection[] {
  const account = BASE_SETTINGS_SECTIONS[0]
  const placeholders = placeholderSections()
  return [account, ...placeholders]
}

export function getSettingsSection(segment: string): SettingsSection | undefined {
  return getSettingsSections().find((section) => section.segment === segment)
}

export type SettingsNavItem = {
  path: string
  label: string
  icon: string
  description?: string
}

export function getSettingsNavItems(): SettingsNavItem[] {
  return getSettingsSections().map((section) => ({
    path: `/settings/${section.segment}`,
    label: section.label,
    icon: section.icon,
    description: section.description,
  }))
}

export type SettingsLocationState = {
  /** Pathname + search (+ hash) when entering settings. */
  from?: string
  /** Mobile chat/page pane to restore on Back to app. */
  mobilePane?: 'chat' | 'page'
}

export function buildSettingsEnterState(args: {
  from: string
  mobilePane?: 'chat' | 'page'
}): SettingsLocationState {
  const state: SettingsLocationState = { from: args.from }
  if (args.mobilePane) {
    state.mobilePane = args.mobilePane
  }
  return state
}

export function isSettingsPath(pathname: string): boolean {
  return pathname === '/settings' || pathname.startsWith('/settings/')
}

/** Pathname from a stored return location (`/path?query` or full URL). */
export function pathnameFromReturnLocation(location: string): string {
  const trimmed = location.trim()
  if (!trimmed) return ''
  try {
    return new URL(trimmed, 'http://local').pathname
  } catch {
    return trimmed.split('?')[0]?.split('#')[0] ?? trimmed
  }
}
